// filepath: apps/web/src/features/lists/gantt/types.ts
//
// FR-UI-VIEW-3 — local wire-shape aliases for the Gantt module.
//
// Mirrors the kanban/tree module conventions:
//   - The wire shape lives here (not in the parent types barrel) so
//     the gantt module stays self-contained and a gantt-only change
//     doesn't ripple into the broader types barrel.
//   - The dispatcher (EntityListPage) selects <GanttView/> only when
//     `default_view_type === "gantt"` — the optional `gantt` block
//     on the wire is consumed by GanttView (and only by GanttView)
//     once the dispatch has fired.
//
// SECURITY / INVARIANT REMINDERS:
//   - INV-2 (server-stripped fields): GanttRecord.fields carries
//     exactly what the server emitted. The renderer iterates
//     `Object.keys(record.fields)` — never reads from a definition-side
//     schema to fill in missing values.
//   - INV-7 (row_version OCC): rowVersion is required for drag-to-edit
//     bar moves. Records without a usable row_version are dropped at
//     the hook layer (useGanttRecords).

import type { EntityRecordWire } from "../types";

/**
 * Wire shape for `definition_json.view_config` from the Gantt module's
 * perspective. Mirrors `internal/entity/view_config.go:EntityViewConfig`.
 * The `gantt` block carries the gantt-specific parameters
 * (start_field / end_field / label_field / optional depends_on_field).
 *
 * The kanban / tree sub-blocks are typed as `unknown` because the
 * gantt module never reads them — EntityListPage dispatches before
 * GanttView mounts.
 */
export interface EntityViewConfigWire {
  default_view_type: "table" | "kanban" | "gantt" | "tree";
  kanban?: unknown;
  tree?: unknown;
  gantt?: EntityGanttConfigWire;
}

/**
 * Wire shape for the gantt sub-block. Mirrors the JSON-decoded
 * `map[string]any` block on the server — the field NAMES are typed
 * here for clarity but the SPA still re-derives them defensively
 * from the wire response (the renderer never trusts the validator's
 * type-system to be the source of truth; it reads what the wire
 * actually carries).
 */
export interface EntityGanttConfigWire {
  /** Datetime field name marking the bar's start. */
  start_field: string;
  /** Datetime field name marking the bar's end. */
  end_field: string;
  /** String field name used as the bar's label text. */
  label_field: string;
  /**
   * Optional reference-array field for dependency arrows. Defaults
   * to "depends_on" (the system-reserved field per FR-ENT-11) when
   * absent. The renderer is permissive — a record whose depends_on
   * value references an id NOT present in the loaded record set is
   * gracefully no-op'd (no orphan arrow, no crash).
   */
  depends_on_field?: string;
}

/**
 * A single normalised Gantt record. Mirrors TreeRecord's shape
 * exactly so the gantt / tree modules share the normaliser
 * conventions. The `start` / `end` ISO date strings are NOT
 * persisted on the record type — they're computed lazily by
 * GanttView via `Object.keys(record.fields)` so the SPA never
 * re-derives them from a definition-side schema.
 */
export interface GanttRecord {
  /** Record UUID. */
  id: string;
  /**
   * Row version (INV-7 OCC token) required for drag-to-edit bar
   * moves. Records without a usable row_version are dropped at the
   * normaliser layer — the renderer cannot safely re-send them.
   */
  rowVersion: number;
  /**
   * Server-stripped fields map. INV-2: the renderer MUST iterate
   * `Object.keys(record.fields)` and never read from a per-row GET
   * to "fill in" absent fields.
   */
  fields: Readonly<Record<string, unknown>>;
}

/**
 * normaliseGanttRecord — project an EntityRecordWire into GanttRecord.
 *
 * Mirrors normaliseTreeRecord's pattern: the row_version is read from
 * either wire casing (`row_version` or `RowVersion`) and coerced to
 * a positive integer. Records without a usable row_version are
 * dropped (null) — the hook layer filters nulls out so the renderer
 * never sees them.
 *
 * The fields map carries every server-stripped field value verbatim
 * — INV-2 forbids the SPA from filling in missing fields via a
 * per-row GET.
 */
export function normaliseGanttRecord(r: EntityRecordWire): GanttRecord | null {
  const rawVersion = (r as unknown as { row_version?: unknown }).row_version;
  const altRawVersion = r.RowVersion;
  const rowVersion =
    typeof rawVersion === "number"
      ? rawVersion
      : typeof rawVersion === "string" && /^[0-9]+$/.test(rawVersion)
        ? Number.parseInt(rawVersion, 10)
        : typeof altRawVersion === "number"
          ? altRawVersion
          : typeof altRawVersion === "string" && /^[0-9]+$/.test(altRawVersion)
            ? Number.parseInt(altRawVersion, 10)
            : null;
  // INV-7 — row_version 0 is treated as "no token" because 0 is not
  // a valid OCC counter.
  if (rowVersion === null || rowVersion <= 0) return null;

  const fieldsMap = (r.Fields ?? {}) as Record<string, unknown>;
  return {
    id: r.ID,
    rowVersion,
    fields: fieldsMap,
  };
}

/**
 * readDatetimeField — extract an ISO-8601 string from a record's
 * fields map for a given field name. The gantt validator pins
 * start_field / end_field to FieldTypeDatetime, so the wire shape
 * is always an ISO string (or null when the field is unset).
 *
 * Returns null when:
 *   - the field is absent from the server-stripped fields map
 *   - the field value is null
 *   - the field value is not a non-empty string
 *
 * The renderer treats null as "no start" / "no end" — see
 * GanttView for the null-date indicator (badge "no start") and
 * the zero-duration point marker (12×12 dot at start x).
 */
export function readDatetimeField(
  record: GanttRecord,
  fieldName: string,
): string | null {
  if (fieldName.length === 0) return null;
  const raw = record.fields[fieldName];
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "string") return null;
  if (raw.length === 0) return null;
  return raw;
}

/**
 * readLabelField — extract the bar's label text from the label_field.
 * Returns the literal string when the value is a non-empty string;
 * returns null otherwise (the renderer falls back to a "<record
 * title>" placeholder in that case — mirrors TreeView's behaviour).
 */
export function readLabelField(
  record: GanttRecord,
  fieldName: string,
): string | null {
  if (fieldName.length === 0) return null;
  const raw = record.fields[fieldName];
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "string") return null;
  if (raw.length === 0) return null;
  return raw;
}

/**
 * readDependsOnField — extract the depends_on reference-array ids
 * from the depends_on field. Returns an empty array when:
 *   - the field is absent (record has no dependencies)
 *   - the field value is null / not an array
 *   - the field value is an empty array
 *
 * The renderer is permissive: ids that do not match a record in the
 * loaded set are silently dropped (no orphan arrow). The field name
 * defaults to "depends_on" (the FR-ENT-11 system-reserved name) but
 * is taken from `viewConfig.gantt.depends_on_field` when supplied.
 */
export function readDependsOnField(
  record: GanttRecord,
  fieldName: string,
): readonly string[] {
  if (fieldName.length === 0) return [];
  const raw = record.fields[fieldName];
  if (raw === null || raw === undefined) return [];
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const entry of raw) {
    if (typeof entry === "string" && entry.length > 0) {
      out.push(entry);
    }
  }
  return out;
}
